import type { OpportunityRelatedDeal } from '~/hooks/apis/offers/useGetOpportunityRelated';

import type { ChecklistStatus, RecordLike } from '../../shared/checklistUtils';
import {
  getPackageDisplayName,
  getStatusLabel,
  isFiniteNumber,
  mapOpportunityDealsByPackageId,
  readNumber,
  readString,
} from '../../shared/checklistUtils';

import type { DiscountsMarginChecklistRow } from './types';

const DISCOUNTS_MARGIN_FIELDS = {
  offerDiscounts: 'Offer discounts',
} as const;

const DEAL_OFFER_DISCOUNT_FIELD = 'offer_discount__c';

export function buildDiscountsMarginRows({
  opportunityDeals,
  packages,
}: {
  opportunityDeals?: Array<OpportunityRelatedDeal>;
  packages?: Array<App.Package>;
}): Array<DiscountsMarginChecklistRow> {
  const packageRows = packages ?? [];
  const opportunityDealsByPackageId = mapOpportunityDealsByPackageId(opportunityDeals);

  return [buildOfferDiscountsRow({ opportunityDealsByPackageId, packages: packageRows })];
}

function buildOfferDiscountsRow({
  opportunityDealsByPackageId,
  packages,
}: {
  opportunityDealsByPackageId: Map<string, OpportunityRelatedDeal>;
  packages: Array<App.Package>;
}): DiscountsMarginChecklistRow {
  const discountDetails = packages.map((offerPackage) => {
    const packageRecord = offerPackage as unknown as RecordLike;
    const packageId = readString(packageRecord, ['id_salesforce_external']);
    const opportunityDeal = packageId ? opportunityDealsByPackageId.get(packageId) : undefined;
    const dealRecord = (opportunityDeal ?? {}) as unknown as RecordLike;
    const discount = readNumber(dealRecord, [DEAL_OFFER_DISCOUNT_FIELD]);

    return {
      discount,
      name: getPackageDisplayName(offerPackage),
    };
  });
  const hasPackages = discountDetails.length > 0;
  const hasAllDiscounts = discountDetails.every(({ discount }) => isFiniteNumber(discount));
  const hasOutOfRangeDiscount = discountDetails.some(
    ({ discount }) => isFiniteNumber(discount) && (discount < 15 || discount > 75),
  );
  const status: ChecklistStatus =
    hasPackages && hasAllDiscounts && !hasOutOfRangeDiscount ? 'approved' : 'requires approval';

  return {
    details: discountDetails.map(({ discount, name }) => ({
      label: name,
      value: isFiniteNumber(discount) ? `${discount}%` : 'Not available',
    })),
    field: DISCOUNTS_MARGIN_FIELDS.offerDiscounts,
    id: 'discounts-margin:offer-discounts',
    rule: 'If any offer discount is greater than 75% or less than 15%, flag for approval; otherwise auto-approve.',
    sourceValue: hasPackages
      ? discountDetails
          .map(({ discount, name }) => `${name}: ${isFiniteNumber(discount) ? `${discount}%` : 'N/A'}`)
          .join('; ')
      : 'No deal options available',
    status,
    statusLabel: getStatusLabel(status),
    summary:
      hasPackages && hasAllDiscounts && !hasOutOfRangeDiscount
        ? 'Offer discounts are within the approved range.'
        : 'Offer discounts need review.',
    title: DISCOUNTS_MARGIN_FIELDS.offerDiscounts,
  };
}
